import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SiteLayoutComponent } from '../../core/layout/site/site-layout.component';
import { AppComponent } from '../../app.component';
import { TokenDataProviderService } from '../../core/services/token-data-provider.service';
import { CurrentUserViewModel } from '../../core/models/current-user.view-model';
import { ActionsService, FooterActionContext } from './actions.service';
import { Router } from '@angular/router';
import { ChatDataProviderService } from '../../features/chat/chat.data-provider.service';
import { Subscription } from 'rxjs';
import {getMainNavigationItems, isMainNavigationItemActive, MainNavigationItem} from '../navigation/main-navigation.config';
import {MobileExperienceShellService} from '../../core/mobile/mobile-experience-shell.service';
import {MobileSearchOverlayService} from '../../core/mobile/mobile-search-overlay.service';

@Component({
  selector: 'app-footer',
  styleUrls: ['./app.footer.component.scss'],
  templateUrl: './app.footer.component.html',
})
export class FooterComponent implements OnInit, OnDestroy {
  @Input() public user: CurrentUserViewModel;
  @ViewChild('searchInput') searchInputViewChild: ElementRef;
  count: number = 0;
  public unreadChatCount = 0;
  public readonly navigationItems = getMainNavigationItems()
    .filter((item) => item.showInFooter)
    .sort((left, right) => (left.footerOrder || 0) - (right.footerOrder || 0));
  public readonly navigationItemsBeforeAdd = this.navigationItems.filter((item) => item.key !== 'marketplace');
  public readonly navigationItemsAfterAdd = this.navigationItems.filter((item) => item.key === 'marketplace');
  public mobileAction: FooterActionContext | null = null;
  public useContextualAction = false;
  private subscriptions: Subscription[] = [];

  public constructor(
    public app: AppComponent,
    public appMain: SiteLayoutComponent,
    public actionsService: ActionsService,
    public ts: TokenDataProviderService,
    private readonly chatDPS: ChatDataProviderService,
    private readonly mobileExperienceShell: MobileExperienceShellService,
    private readonly mobileSearchOverlay: MobileSearchOverlayService,
    private router: Router,
  ) {
  }

  public ngOnInit(): void {
    this.subscriptions.push(this.chatDPS.unreadCount$.subscribe((count) => {
      this.unreadChatCount = count;
    }));
    this.subscriptions.push(this.actionsService.contextualAction$.subscribe(() => this.refreshMobileAction()));
    this.subscriptions.push(this.mobileExperienceShell.active$.subscribe(() => this.refreshMobileAction()));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public handleSearchButtonClick(event: Event) {
    event.preventDefault();
    if (this.appMain.mobileTopNavigationActive) {
      this.mobileSearchOverlay.open();
      return;
    }
    this.appMain.onTopbarMobileButtonClick(event);
    this.appMain.onTopbarItemClick(event, 'search');
  }

  public handleFooterActionClick(): void {
    if (!this.appMain.mobileTopbarActive && this.appMain.activeTopbarItem !== 'notifications') {
      return;
    }
    this.appMain.mobileTopbarActive = false;
    this.appMain.activeTopbarItem = null;
    this.appMain.enableBodyScroll();
  }

  navigateToProfileOrGarage(event: Event) {
    event.preventDefault();
    this.handleFooterActionClick();
    this.count++;
    setTimeout(() => {
      if (this.count === 1) {
        this.count = 0;
        const profileUrl = `/${this.user.slug}/profile`;
        const garageUrl = `/${this.user.slug}/garage`;
        const currentUrl = this.router.url;

        // Check if we are on profile (check strict match or close enough if slug works as profile)
        // Adjusting logic: if URL contains profile path OR equals slug path
        const isOnProfile = currentUrl.includes(profileUrl) || currentUrl === `/${this.user.slug}`;
        const isOnGarage = currentUrl.includes(garageUrl);

        if (isOnProfile) {
          this.router.navigate([garageUrl]);
        } else if (isOnGarage) {
          this.router.navigate([profileUrl]);
        } else {
          this.router.navigate([profileUrl]);
        }
      }

      if (this.count > 1) {
        this.count = 0;
        this.router.navigate(['/', this.user.slug, 'garage']);
      }
    }, 250);
  }

  get isActive(): boolean {
    return this.router.url.includes(`/${this.user.slug}`);
  }

  public isNavigationItemActive(item: MainNavigationItem): boolean {
    return isMainNavigationItemActive(this.router.url, item);
  }

  public isCustomFooterIcon(item: MainNavigationItem): boolean {
    return item.footerIconKind === 'vehicles-combo';
  }

  public async runMobileAction(): Promise<void> {
    this.handleFooterActionClick();
    await this.actionsService.executeContextualAction();
  }

  private refreshMobileAction(): void {
    this.useContextualAction = this.mobileExperienceShell.isActive;
    this.mobileAction = this.useContextualAction ? this.actionsService.contextualAction : null;
  }
}
